import { lazy, Suspense, useEffect, useState } from "react";

import { HttpStatusCode } from "axios";
import { Button, Input, Select, useDisclosure, useToast } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";

import AddIcon from "@/assets/AddIcon";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, DURATION_TOAST } from "@/common/constants";
import Loader from "@/components/Layouts/Loader";
import ModalDelete from "@/components/Modals/ModalDelete";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import { deleteRefData, getListOfTypeRefData, getListReferenceData } from "@/services/referenceDataService";

import '../styles/ListPage.css';

const TableReferenceData = lazy(() => import('@/components/Tables/TableReferenceData'));

const ReferenceDataList = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const name = useAppSelector((state: RootState) => state.account.name);

    const [currentPageNumber, setCurrentPageNumber] = useState(DEFAULT_PAGE_NUMBER);
    const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [dataList, setDataList] = useState([]);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [idToDelete, setIdToDelete] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [searchValue, setSearchValue] = useState('');
    const [totalPageNumber, setTotalPageNumber] = useState(1);
    const [typeValue, setTypeValue] = useState('');
    const [listType, setListType] = useState([]) as any;

    const handleDelete = (id: string) => {
        setIdToDelete(id);
        onOpen();
    };

    const handleConfirmDelete = async () => {
        try {
            const obj = {
                id: idToDelete,
                modifiedBy: name
            };
            const response = await deleteRefData(obj) as any;
            if (response.status === HttpStatusCode.Ok) {
                await getData();
                toast({
                    title: 'Data deleted.',
                    description: "We've deleted the data.",
                    status: 'success',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            } else {
                showToast({ code: 'Delete Failed', message: "We failed to delete the data." });
            }
        } catch (error) {
            showToast({ code: 'Delete Error', message: "An error occurred while deleting." });
        } finally {
            onClose();
        }
    };

    const showToast = (error: any) => {
        toast({
            title: error?.status,
            description: error?.message,
            status: 'error',
            duration: DURATION_TOAST,
            isClosable: true,
        });
    };

    const handleChange = (setter: React.Dispatch<React.SetStateAction<any>>) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setter(e.target.value);
    };

    const handleSearch = () => {
        setCurrentPageNumber(1);
        setTimeout(() => {
            getData();
        }, 300);
    };

    const getData = async () => {
        setIsLoading(true);
        const param = { type: typeValue, search: searchValue, pageNumber: currentPageNumber, pageSize: currentPageSize };

        try {
            const response = await getListReferenceData(param) as any;
            if (response.status === HttpStatusCode.Ok) {
                setDataList(response.data);
                setTotalPageNumber(response.totalPage);
                setHasNextPage(response.hasNextPage);
            } else {
                showToast(response);
            }
        } catch (error) {
            showToast({ code: 'Error', message: 'Failed to fetch data' });
        } finally {
            setIsLoading(false);
        }
    };

    const getDataListType = async () => {
        const response = await getListOfTypeRefData() as any;
        if (response.status === HttpStatusCode.Ok) {
            setListType(response.data);
        }
    };

    useEffect(() => {
        getDataListType();
    }, []);

    useEffect(() => {
        getData();
    }, [currentPageSize, currentPageNumber]);

    return (
        <>
            <ModalDelete title="Delete it?" isOpen={isOpen} onClose={onClose} handleConfirmDelete={handleConfirmDelete} />
            <div className="table-container">
                <div className="table-filter-action-container">
                    <div className="table-filter-container">
                        <div className="filter-input-width-5">
                            <Input
                                placeholder='Search'
                                onChange={handleChange(setSearchValue)}
                            />
                        </div>
                        <div className="filter-input-width-5">
                            <Select
                                placeholder="All Type"
                                style={{ backgroundColor: 'white' }}
                                onChange={(e) => setTypeValue(e.target.value)}
                            >
                                {listType.map((item: any) => {
                                    return (
                                        <option value={item}>{item}</option>
                                    )
                                })}
                            </Select>
                        </div>
                        <div>
                            <Button
                                className='button table-search-button'
                                onClick={handleSearch}
                            >
                                Search
                            </Button>
                        </div>
                        <div className="table-filter-separator"/>
                        <Button
                            leftIcon={<AddIcon />}
                            className='button table-add-button'
                            onClick={() => navigate('/settings/reference-data/add')}
                        >
                            Add Reference Data
                        </Button>
                    </div>
                </div>
                <Suspense fallback={<Loader />}>
                    <TableReferenceData
                        dataList={dataList}
                        isLoading={isLoading}
                        handleEdit={(id: string) => navigate('/settings/reference-data/edit', { state: { id } })}
                        handleDelete={handleDelete}
                        currentPageSize={currentPageSize}
                        handleChangePageSize={setCurrentPageSize}
                        handleChangePageNumber={setCurrentPageNumber}
                        currentPageNumber={currentPageNumber}
                        totalPageNumber={totalPageNumber}
                        hasNextPage={hasNextPage}
                    />
                </Suspense>
            </div>
        </>
    )
}

export default ReferenceDataList;
